import React, {useReducer, useEffect, useMemo, useLayoutEffect} from 'react';
import { useForm, FormProvider } from "react-hook-form";
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup'
import { ParticipantSchema } from '../../components/ParticipantForm'

const TemplateStateContext = React.createContext();
const TemplateDispatchContext = React.createContext();

export const SET_TEMPLATE = "SET_TEMPLATE";
export const UPDATE_TEMPLATE = "UPDATE_TEMPLATE"; 
export const UPDATE_ELEMENT = "UPDATE_ELEMENT"; 
export const SET_CURRENT_ELEMENT = "SET_CURRENT_ELEMENT"; 
export const UPDATE_ELEMENT_LEVELS = "UPDATE_ELEMENT_LEVELS"; 
export const UPDATE_DYNAMIC_STATEMENTS = "UPDATE_DYNAMIC_STATEMENTS"; 
export const SET_TOTAL_SCORE = "SET_TOTAL_SCORE"; 
export const SET_PARTICIPANTS = "SET_PARTICIPANTS"; 
export const RESET = "RESET"; 
const SET_STATE = "SET_STATE"; 

const reducer = (state, action) => {

  switch(action.type) {
    case UPDATE_TEMPLATE:
      return {
        ...state,
        template: {
          ...state.template,
          ...action.template
        }
      }
    case SET_TEMPLATE:
      const elementLevels = {}
      const elements = action.template?.elements?.map(element => {
        if (element.isDynamic) {
          const firstLevel = element.dynamicElement.levels[0];
          elementLevels[element._id] = firstLevel;
          return {
            ...element,
            content: firstLevel.content
          }
        }

        return element;
      })

      return {
        ...state,
        template: action.template,
        templateId: action.template._id,
        elements: elements,
        participants: action.template.participants,
        currentElement: '',
        elementLevels,
      }
    case UPDATE_ELEMENT:
        return {
          ...state,
          elements: state.elements.map(currentElement => {
            if (currentElement._id === action.element._id) {
              return {
                ...currentElement,
                ...action.element
              }
            }

            return currentElement
          })
        }
    case SET_CURRENT_ELEMENT:
        return {
          ...state,
          currentElement: action.elementId
        }
    case SET_TOTAL_SCORE:
      return {
        ...state,
        totalScore: action.totalScore
      }
    case UPDATE_ELEMENT_LEVELS:
        return {
          ...state,
          elementLevels:  {
            ...state.elementLevels,
            [action.elementId]: action.level
          }
        }
    case UPDATE_DYNAMIC_STATEMENTS:

        const dynamicStatements = {};
        return {
          ...state,
          elements: state.elements.map(currentElement => {
            if (currentElement.type === 'statement' && currentElement.isDynamic) {
              const newElement = currentElement.dynamicElement.levels.find(l => l.number === action.levelNumber)
              dynamicStatements[currentElement._id] = newElement;
              return {
                ...currentElement,
                content: newElement.content
              }
            }

            return currentElement
          }),
          elementLevels:  {
            ...state.elementLevels,
            ...dynamicStatements
          }
        }
    case SET_PARTICIPANTS: 
        return {
          ...state,
          participants: action.participants
        }
    case RESET: 
        return initialState
    case SET_STATE: 
        return action.state
    default:
      return state
  }
}

const initialState = {
  template: {},
  templateId: '',
  elements: [],
  participants: [],
  currentElement: '',
  elementLevels: {},
  totalScore: 0
}

function getTotalScore(elements) {
  return elements?.reduce((total, element) => {

      if (element.isDynamic) {
          const sortedLevels = element.dynamicElement.levels.sort((a,b)=> b.number - a.number )
          if (!element.dynamicElement.hasSameContent) {
            total += sortedLevels[0].score;
          }
      }

      return total
  }, 0)
}

const ContractSchema = Yup.object().shape({
  language: Yup.string(),
  shield: Yup.string(),
  fillMyData: Yup.string(),
  participants: Yup.array().of(ParticipantSchema)
})


function TemplateProvider({children}) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const formMethods = useForm({
    resolver: yupResolver(ContractSchema)
});

  const totalScore = useMemo(() => {
    return getTotalScore(state.elements)
  }, [state.elements])

  useLayoutEffect(() => {
    const savedData = localStorage.getItem('create-contract-cache');
    if (savedData) {
      const parsedData = JSON.parse(savedData);
      dispatch({ type: SET_STATE, state: parsedData })
    }
    
  }, [dispatch])

  return (
    <FormProvider {...formMethods}>
      <TemplateStateContext.Provider value={{...state, totalScore}}>
        <TemplateDispatchContext.Provider value={dispatch}>
          {children}
        </TemplateDispatchContext.Provider>
      </TemplateStateContext.Provider>
    </FormProvider>
  )
}


function useTemplateState() {
  const context = React.useContext(TemplateStateContext)
  if (context === undefined) {
    throw new Error('useTemplateState must be used within a TemplateListProvider')
  }
  return context
}

function useTemplateDispatch() {
  const context = React.useContext(TemplateDispatchContext)
  if (context === undefined) {
    throw new Error('useTemplateDispatch must be used within a TemplateProvider')
  }
  return context
}

function useTemplate() {
  return [useTemplateState(), useTemplateDispatch()]
}

export { TemplateProvider, useTemplate, useTemplateState, useTemplateDispatch }